import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../../common/Button';
import PatientService from '../../../services/patientService';
import DentalNoteService from '../../../services/dentalNoteService';
import ProcedureService from '../../../services/procedureService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import './DentalNotes.css';

const DentalNotes = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();

  // Función para formatear fecha de manera amigable
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Hoy';
    } else if (diffDays === 2) {
      return 'Ayer';
    } else if (diffDays <= 7) {
      return `Hace ${diffDays - 1} días`;
    } else {
      return date.toLocaleDateString('es-ES', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    }
  };

  // Función para formatear hora
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const [notes, setNotes] = useState([]);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const [viewMode, setViewMode] = useState(() => {
    // Recuperar preferencia guardada o usar 'list' por defecto (mejor para datos médicos)
    return localStorage.getItem('dental-notes-view-mode') || 'list';
  }); // 'list' | 'grid'
  
  // Detect if mobile and force grid view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Force grid view on mobile
  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  // Load dental notes from API
  const loadDentalNotes = React.useCallback(async () => {
    if (patientId && auth.user?.access_token) {
      try {
        setLoading(true);
        const response = await DentalNoteService.getDentalNotesByPatient(
          patientId, 
          { status: 'active', limit: 50 }, 
          auth.user.access_token
        );
        
        const notesData = response.data || [];
        
        // Cargar información de procedimientos para las notas que los tienen
        const notesWithProcedures = await Promise.all(
          notesData.map(async (note) => {
            if (note.procedureId) {
              try {
                const procedure = await ProcedureService.getProcedureById(note.procedureId, auth.user.access_token);
                return { ...note, procedureName: procedure.name, procedureCost: procedure.cost };
              } catch (error) {
                console.error('Error loading procedure for note:', note.id, error);
                return { ...note, procedureName: 'Procedimiento no encontrado' };
              }
            }
            return note;
          })
        );
        
        setNotes(notesWithProcedures);
      } catch (error) {
        console.error('Error loading dental notes:', error);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    }
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    loadDentalNotes();
  }, [loadDentalNotes]);

  // Removed automatic reload on window focus to prevent unnecessary API calls

  useEffect(() => {
    const loadPatient = async () => {
      if (patientId && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(patientId, auth.user.access_token);
          setPatient(patientData);
        } catch (error) {
          console.error('Error loading patient:', error);
        }
      }
    };
    loadPatient();
  }, [patientId, auth.user?.access_token]);

  // No longer needed since we use API instead of localStorage

  const openNewNotePage = () => {
    navigate(`/patients/${patientId}/dental-notes/new`);
  };

  // Función para cambiar modo de vista y guardar preferencia
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('dental-notes-view-mode', mode);
  };

  // Función para truncar texto inteligentemente (por palabras)
  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    
    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    // Si hay un espacio cerca del final, cortamos por palabra completa
    if (lastSpaceIndex > maxLength * 0.8) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    // Si no, cortamos por carácter
    return truncated + '...';
  };

  // Límites de caracteres según el modo de vista - optimizados para lectura
  const getCharacterLimit = () => {
    return viewMode === 'list' ? 200 : 140;
  };





  return (
    <div className="dental-notes-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Odontograma"
        actionButton={
          <Button 
            variant="primary" 
            icon="fas fa-plus" 
            onClick={openNewNotePage}
          >
            Agregar nota
          </Button>
        }
      />

      <div className="notes-list-section">
        {loading ? (
          <div className="empty-state">
            <i className="fas fa-spinner fa-spin"></i>
            <h3>Cargando odontograma...</h3>
          </div>
        ) : notes.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-sticky-note"></i>
            <h3>No hay notas aún</h3>
            <p>Haz clic en "Agregar nota" para registrar observaciones usando el odontograma.</p>
          </div>
        ) : (
          <>
            {notes.length >= 1 && (
              <div className="notes-header">
                <div className="notes-count">
                  <i className="fas fa-list"></i>
                  {notes.length} nota{notes.length > 1 ? 's' : ''} registrada{notes.length > 1 ? 's' : ''}
                </div>
                <div className="header-controls">
                  {/* View Mode Toggle - Hidden on mobile */}
                  {!isMobile && (
                    <div className="view-toggle">
                      <button 
                        className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('list')}
                        title="Vista de lista"
                      >
                        <i className="fas fa-list"></i>
                      </button>
                      <button 
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('grid')}
                        title="Vista de cuadrícula"
                      >
                        <i className="fas fa-th"></i>
                      </button>
                    </div>
                  )}
                  
                  {/* Sort Controls */}
                  {notes.length > 1 && (
                    <div className="sort-controls">
                      <span className="sort-label">Ordenar:</span>
                      <button 
                        className={`sort-btn ${sortOrder === 'newest' ? 'active' : ''}`}
                        onClick={() => setSortOrder('newest')}
                      >
                        <i className="fas fa-arrow-down"></i>
                        Reciente
                      </button>
                      <button 
                        className={`sort-btn ${sortOrder === 'oldest' ? 'active' : ''}`}
                        onClick={() => setSortOrder('oldest')}
                      >
                        <i className="fas fa-arrow-up"></i>
                        Antiguo
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={`notes-container ${effectiveViewMode}`}>
              {notes
                .sort((a, b) => {
                  const dateA = new Date(a.createdAt);
                  const dateB = new Date(b.createdAt);
                  return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
                })
                .map(note => (
              <div key={note.id} className={`note-card ${effectiveViewMode}-card`}>
                {effectiveViewMode === 'list' ? (
                  // List Layout
                  <>
                    {/* Left side: Date and horizontal metadata */}
                    <div className="note-left-horizontal">
                      <div className="note-date-section">
                        <div className="timestamp">{formatDate(note.createdAt)}</div>
                        <div className="time">{formatTime(note.createdAt)}</div>
                      </div>
                      <div className="note-meta-horizontal">
                        <span className="badge mode-compact">
                          <i className={`fas ${note.mode === 'adult' ? 'fa-user' : 'fa-child'}`}></i>
                          {note.mode === 'adult' ? 'Adulto' : 'Pediátrico'}
                        </span>
                        
                        {/* Información legible según el tipo de nota */}
                        {note.isMultiDiagnosis ? (
                          // Nota con múltiples diagnósticos
                          <>
                            <span className="badge diagnosis-readable">
                              {note.diagnosisCount} diagnóstico{note.diagnosisCount > 1 ? 's' : ''}
                            </span>
                            <span className="badge selections-readable">
                              {note.selectionsCount} selección{note.selectionsCount > 1 ? 'es' : ''}
                            </span>
                          </>
                        ) : (
                          // Nota tradicional (1 diagnóstico)
                          <>
                            <span className="badge diagnosis-readable">
                              1 diagnóstico
                            </span>
                            {note.selections && note.selections.length > 0 && (
                              <span className="badge selections-readable">
                                {note.selections.length} selección{note.selections.length > 1 ? 'es' : ''}
                              </span>
                            )}
                            {note.procedureName && (
                              <span className="badge procedure-readable" title={note.procedureName}>
                                {note.procedureName.length > 15 ? `${note.procedureName.substring(0, 15)}...` : note.procedureName}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Center: Content descriptivo */}
                    <div className="note-content">
                      
                      {/* Mostrar texto de nota si existe */}
                      {note.text && note.text.trim() !== '' && (
                        <div className="note-text">
                          {truncateText(note.text, getCharacterLimit())}
                        </div>
                      )}
                      
                    </div>
                    
                    {/* Right side: Action button */}
                    <div className="note-right">
                      <Button 
                        variant="outline"
                        size="sm"
                        icon="fas fa-edit"
                        onClick={() => navigate(`/patients/${patientId}/dental-notes/${note.id}/edit`)} 
                        title="Editar en odontograma"
                      >
                        Editar
                      </Button>
                    </div>
                  </>
                ) : (
                  // Grid Layout
                  <>
                    <div className="grid-note-meta-compact">
                      <div className="grid-badges-row">
                        <span className="badge mode-compact">
                          <i className={`fas ${note.mode === 'adult' ? 'fa-user' : 'fa-child'}`}></i>
                          {note.mode === 'adult' ? 'Adulto' : 'Pediátrico'}
                        </span>
                        
                        {/* Información legible según el tipo de nota */}
                        {note.isMultiDiagnosis ? (
                          // Nota con múltiples diagnósticos
                          <>
                            <span className="badge diagnosis-readable">
                              {note.diagnosisCount} diagnóstico{note.diagnosisCount > 1 ? 's' : ''}
                            </span>
                            <span className="badge selections-readable">
                              {note.selectionsCount} selección{note.selectionsCount > 1 ? 'es' : ''}
                            </span>
                          </>
                        ) : (
                          // Nota tradicional (1 diagnóstico)
                          <>
                            <span className="badge diagnosis-readable">
                              1 diagnóstico
                            </span>
                            {note.selections && note.selections.length > 0 && (
                              <span className="badge selections-readable">
                                {note.selections.length} selección{note.selections.length > 1 ? 'es' : ''}
                              </span>
                            )}
                            {note.procedureName && (
                              <span className="badge procedure-readable" title={note.procedureName}>
                                {note.procedureName.length > 12 ? `${note.procedureName.substring(0, 12)}...` : note.procedureName}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      
                    </div>
                    {/* Solo mostrar texto si existe */}
                    {note.text && note.text.trim() !== '' && (
                      <div className="note-text">
                        {truncateText(note.text, getCharacterLimit())}
                      </div>
                    )}
                    
                    <div className="grid-note-footer">
                      <div className="timestamp-container">
                        <span className="timestamp">{formatDate(note.createdAt)}</span>
                        <span className="time">{formatTime(note.createdAt)}</span>
                      </div>
                      <div className="note-actions">
                        <Button 
                          variant="outline"
                          size="sm"
                          icon="fas fa-edit"
                          onClick={() => navigate(`/patients/${patientId}/dental-notes/${note.id}/edit`)} 
                          title="Editar en odontograma"
                        >
                          Editar
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              ))}
            </div>
          </>
        )}
      </div>


    </div>
  );
};

export default DentalNotes;


